<template>
  <div
    id="galleryModal"
    ref="modal"
    class="modal fade"
    tabindex="-1"
    role="dialog"
    aria-labelledby="galleryModalLabel"
    aria-hidden="true"
    data-backdrop="static"
    data-keyboard="false"
  >
    <div
      class="modal-dialog modal-xl gallery-modal__dialog"
      role="document"
    >
      <div class="modal-content gallery-modal__content">
        <!-- Header -->
        <div class="modal-header gallery-modal__header">
          <div class="gallery-modal__header-top">
            <div class="gallery-modal__title-row">
              <h5
                id="galleryModalLabel"
                class="modal-title gallery-modal__title"
              >
                Add from Design Gallery
              </h5>
              <button
                type="button"
                class="close gallery-modal__close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <p class="gallery-modal__subtitle">
              Pick one or more templates to add to this placement.
            </p>
          </div>

          <!-- Type filter tabs -->
          <div
            v-if="typeKeys.length > 0"
            class="gallery-modal__typebar"
          >
            <button
              v-for="key in typeKeys"
              :key="key"
              type="button"
              class="gallery-modal__type-tab"
              :class="{ 'gallery-modal__type-tab--active': activeTypeKey === key }"
              @click="setTypeKey(key)"
            >
              {{ typeLabel(key) }}
            </button>
          </div>

          <!-- Template Access filter -->
          <div class="gallery-modal__access-row">
            <span class="gallery-modal__filterlabel">Template Access</span>
            <div class="gallery-modal__style-options">
              <button
                type="button"
                class="gallery-modal__style-option"
                :class="{ 'gallery-modal__style-option--active': accessFilters.includes('global') }"
                @click="toggleAccessFilter('global')"
              >
                Global
              </button>
              <button
                type="button"
                class="gallery-modal__style-option"
                :class="{ 'gallery-modal__style-option--active': accessFilters.includes('company') }"
                @click="toggleAccessFilter('company')"
              >
                My Company
              </button>
            </div>
          </div>
        </div>

        <!-- Body -->
        <div class="modal-body gallery-modal__body">
          <!-- Loading -->
          <div
            v-if="isLoading"
            class="gallery-modal__state"
          >
            <span
              class="spinner-border text-primary"
              role="status"
            />
            <p class="mt-2 text-muted small">
              Loading templates…
            </p>
          </div>

          <!-- Error -->
          <div
            v-else-if="error"
            class="gallery-modal__state"
          >
            <p class="text-danger">
              {{ error }}
            </p>
          </div>

          <!-- Empty -->
          <div
            v-else-if="filteredTemplates.length === 0"
            class="gallery-modal__state"
          >
            <p class="text-muted">
              No templates found.
            </p>
          </div>

          <!-- Grid -->
          <div
            v-else
            class="gallery-modal__grid"
          >
            <div
              v-for="tpl in filteredTemplates"
              :key="tpl.id"
              class="gallery-card"
              :class="{ 'gallery-card--selected': isSelected(tpl.id) }"
              role="button"
              tabindex="0"
              :aria-pressed="isSelected(tpl.id) ? 'true' : 'false'"
              :aria-label="tpl.name"
              @click="toggleTemplate(tpl)"
              @keydown.enter.prevent="toggleTemplate(tpl)"
              @keydown.space.prevent="toggleTemplate(tpl)"
            >
              <!-- Thumbnail -->
              <div class="gallery-card__thumb">
                <img
                  v-if="tpl.thumbnail"
                  :src="tpl.thumbnail"
                  :alt="tpl.name"
                  loading="lazy"
                  class="gallery-card__img"
                >
                <span
                  v-else
                  class="gallery-card__placeholder"
                >
                  {{ tpl.width || '?' }}&times;{{ tpl.height || '?' }}
                </span>
              </div>

              <!-- Name -->
              <div
                class="gallery-card__name"
                :title="tpl.name"
              >
                {{ tpl.name }}
              </div>

              <!-- Selected checkmark badge -->
              <div
                v-if="isSelected(tpl.id)"
                class="gallery-card__check"
                aria-hidden="true"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-check"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer gallery-modal__footer">
          <span class="gallery-modal__selection-count text-muted small">
            {{ selectedCount > 0 ? selectedCount + ' selected' : '0 selected' }}
          </span>
          <div class="gallery-modal__footer-actions">
            <button
              type="button"
              class="btn btn-secondary"
              data-dismiss="modal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="selectedCount === 0"
              @click="onAdd"
            >
              {{ selectedCount > 0 ? 'Add ' + selectedCount : 'Add' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

const TYPE_PREFIX = 'type_';
const GALLERY_IMPORT_FROM_PSD_EVENT = 'gallery-import-psd-templates';

function asTagArray(tags) {
  if (!tags) return [];
  return String(tags)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function normalizeKey(k) {
  return String(k || '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');
}

function getTypeKeys(tags) {
  const arr = asTagArray(tags);
  const keys = [];
  arr.forEach((t) => {
    if (t.startsWith(TYPE_PREFIX)) {
      const key = normalizeKey(t.slice(TYPE_PREFIX.length));
      if (key && !keys.includes(key)) keys.push(key);
    }
  });
  return keys;
}

const ACRONYMS = { pdp: 'PDP' };

function typeLabel(key) {
  return normalizeKey(key)
    .split('_')
    .filter(Boolean)
    .map((w) => ACRONYMS[w] || w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const getCompanyTemplateIds = (template) => {
  if (!Array.isArray(template?.companyTemplates)) return [];

  return template.companyTemplates
    .map((companyTemplate) => Number(companyTemplate?.id ?? companyTemplate?.company_id ?? companyTemplate))
    .filter((companyId) => Number.isFinite(companyId) && companyId > 0);
};

export default {
  name: 'AddFromGalleryTemplateModal',
  props: {
    companyId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      allTemplates: [],
      selectedIds: [],
      activeTypeKey: '__all__',
      isLoading: false,
      error: null,
      loaded: false,
      accessFilters: ['company'],
    };
  },

  computed: {
    eligibleTemplates() {
      return this.allTemplates.filter((t) => {
        const isGlobal = !t.customer_id || Number(t.customer_id) === 0;
        const currentCompanyId = this.companyId == null ? null : Number(this.companyId);
        const isSharedWithCompany = currentCompanyId !== null && getCompanyTemplateIds(t).includes(currentCompanyId);
        const isCompany = currentCompanyId !== null && (
          Number(t.company_id) === currentCompanyId ||
          isSharedWithCompany
        );

        return (
          (this.accessFilters.includes('global') && isGlobal) ||
          (this.accessFilters.includes('company') && isCompany)
        );
      });
    },

    typeKeys() {
      const keySet = new Set();
      this.eligibleTemplates.forEach((t) => {
        getTypeKeys(t.tags).forEach((k) => keySet.add(k));
      });
      const keys = Array.from(keySet).sort((a, b) =>
        typeLabel(a).localeCompare(typeLabel(b))
      );
      return ['__all__', ...keys];
    },

    filteredTemplates() {
      if (!this.activeTypeKey || this.activeTypeKey === '__all__') {
        return this.eligibleTemplates;
      }
      return this.eligibleTemplates.filter((t) =>
        getTypeKeys(t.tags).includes(this.activeTypeKey)
      );
    },

    selectedCount() {
      return this.selectedIds.length;
    },
  },

  watch: {
    accessFilters() {
      // Reset activeTypeKey to first available key after access filter changes
      if (!this.typeKeys.includes(this.activeTypeKey)) {
        this.activeTypeKey = this.typeKeys[0] || '__all__';
      }
    },
  },

  mounted() {
    $(this.$refs.modal).on('show.bs.modal', () => {
      this.selectedIds = [];
      if (!this.loaded) {
        this.fetchTemplates();
      }
    });
  },

  beforeDestroy() {
    $(this.$refs.modal).off('show.bs.modal');
  },

  methods: {
    typeLabel(key) {
      return key === '__all__' ? 'All' : typeLabel(key);
    },

    setTypeKey(key) {
      this.activeTypeKey = key;
    },

    isSelected(id) {
      return this.selectedIds.includes(id);
    },

    toggleTemplate(tpl) {
      const idx = this.selectedIds.indexOf(tpl.id);
      if (idx === -1) {
        this.selectedIds.push(tpl.id);
      } else {
        this.selectedIds.splice(idx, 1);
      }
    },

    onAdd() {
      if (!this.selectedCount) return;
      const chosen = this.allTemplates.filter((t) =>
        this.selectedIds.includes(t.id)
      );

      const normalizedTemplates = chosen
        .map(({ id, width, height }) => ({
          id: Number(id),
          width: Number(width),
          height: Number(height),
        }))
        .filter((template) => template.id && template.width && template.height);

      if (!normalizedTemplates.length) {
        toastr.error('Selected templates are missing required dimensions.');
        return;
      }

      const templatesById = new Map(chosen.map((template) => [Number(template.id), template]));
      const templatesWithSourcePsd = normalizedTemplates
        .map((template) => {
          const originalTemplate = templatesById.get(template.id);
          return {
            ...template,
            source_file: originalTemplate?.source_file || null,
            name: originalTemplate?.name || '',
          };
        })
        .filter((template) => template.source_file);

      if (templatesWithSourcePsd.length && templatesWithSourcePsd.length !== normalizedTemplates.length) {
        toastr.error('Select only PSD-backed templates or only regular templates.');
        return;
      }

      if (templatesWithSourcePsd.length) {
        const files = templatesWithSourcePsd.map((template) => ({
          url: template.source_file,
          name: template.name || `Template ${template.id}`,
          id: template.id,
        }));
        const templatesIds = templatesWithSourcePsd.map((template) => Number(template.id)).filter(Boolean);

        window.dispatchEvent(
          new CustomEvent(GALLERY_IMPORT_FROM_PSD_EVENT, {
            detail: {
              files,
              templatesIds,
            },
          })
        );
      } else {
        window.dispatchEvent(
          new CustomEvent('gallery-add-templates', { detail: { templates: normalizedTemplates } })
        );
      }

      $(this.$refs.modal).modal('hide');
    },

    async fetchTemplates() {
      this.isLoading = true;
      this.error = null;
      try {
        const { data } = await axios.get('/networks/templates');
        this.allTemplates = Array.isArray(data) ? data : [];
        this.loaded = true;
        if (this.typeKeys.length > 0) {
          this.activeTypeKey = this.typeKeys[0];
        }
      } catch {
        this.error = 'Failed to load templates. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    toggleAccessFilter(filter) {
      const filters = [...this.accessFilters];
      const index = filters.indexOf(filter);
      if (index === -1) {
        filters.push(filter);
      } else {
        if (filters.length === 1) return; // keep at least one active
        filters.splice(index, 1);
      }
      this.accessFilters = filters;
    },
  },
};
</script>

<style scoped>
/* Dialog sizing */
.gallery-modal__dialog {
  max-width: 900px;
}

/* Header layout */
.gallery-modal__header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #dee2e6;
}
.gallery-modal__header-top {
  width: 100%;
}
.gallery-modal__title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}
.gallery-modal__title {
  font-size: 1rem;
  font-weight: 600;
}
.gallery-modal__close {
  padding: 0;
  margin: 0;
  line-height: 1;
}
.gallery-modal__subtitle {
  margin: 0.25rem 0 0.5rem;
  font-size: 0.75rem;
  color: #6c757d;
}

/* Type filter tabs */
.gallery-modal__typebar {
  display: inline-flex;
  flex-wrap: nowrap;
  gap: 0;
  margin-top: 0.5rem;
  background: #e5e7eb;
  border-radius: 0.375rem;
  padding: 0.2rem;
  overflow-x: auto;
  max-width: 100%;
}
.gallery-modal__type-tab {
  font-size: 0.75rem;
  padding: 0.25rem 0.75rem;
  border-radius: 0.25rem;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  color: #6b7280;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
  white-space: nowrap;
  line-height: 1.5;
}
.gallery-modal__type-tab:hover {
  background: #d1d5db;
}
.gallery-modal__type-tab--active {
  background: #fff;
  border-color: #d1d5db;
  color: #111827;
  font-weight: 600;
}

/* Body */
.gallery-modal__body {
  max-height: 60vh;
  overflow-y: auto;
  padding: 1rem 1.25rem;
}

/* Loading / empty state */
.gallery-modal__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
}

/* Grid */
.gallery-modal__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 1rem;
}

/* Card */
.gallery-card {
  position: relative;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
  outline: none;
}
.gallery-card:hover {
  border-color: #adb5bd;
}
.gallery-card:focus-visible {
  box-shadow: 0 0 0 2px rgb(24, 154, 115);
}
.gallery-card--selected {
  border: 1px solid rgb(24, 154, 115);
  box-shadow: 0 0 0 2px rgba(24, 154, 115, 0.2);
}

/* Thumbnail */
.gallery-card__thumb {
  width: 100%;
  height: 120px;
  overflow: hidden;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
}
.gallery-card__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}
.gallery-card__placeholder {
  font-size: 0.7rem;
  color: #6c757d;
}

/* Name */
.gallery-card__name {
  padding: 0.35rem 2.1rem 0.35rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-top: 1px solid #dee2e6;
  background: #fff;
}

/* Checkmark badge */
.gallery-card__check {
  position: absolute;
  bottom: 0.35rem;
  right: 0.4rem;
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 50%;
  background: rgb(24, 154, 115);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}
.gallery-card__check .lucide-check {
  width: 0.75rem;
  height: 0.75rem;
}

/* Footer */
.gallery-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.25rem;
  border-top: 1px solid #dee2e6;
}
.gallery-modal__footer-actions {
  display: flex;
  gap: 0.5rem;
}
.gallery-modal__selection-count {
  font-size: 0.8rem;
}

.close.gallery-modal__close span {
  padding-right: 15px;
}

/* Template Access filter row */
.gallery-modal__access-row {
  display: flex;
  align-items: center;
  gap: 22px;
  padding: 8px 0 4px;
  flex-wrap: wrap;
}
.gallery-modal__filterlabel {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}
.gallery-modal__style-options {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 28px;
}
.gallery-modal__style-option {
  border: none;
  background: none;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;
}
.gallery-modal__style-option:hover:not(.gallery-modal__style-option--active) {
  color: #334155;
  background: #f1f5f9;
}
.gallery-modal__style-option--active {
  background: #0f172a;
  color: #fff;
  font-weight: 600;
}

</style>

